<template>
	<DataTable
		:value="rows"
		:loading="loading"
		:filters="filters"
		class="p-datatable-sm p-datatable-documents"
		:autoLayout="true"
		columnResizeMode="fit"
		:reorderableColumns="true"
		@row-reorder="onRowReorder"
		@row-dblclick="openEntryDialog($event.data)"
		:scrollable="true"
		scrollHeight="flex"
		:removableSort="true"
		filterDisplay="row"
		v-model:filters="filters"
		@filter="onFilter($event)"
		@sort="onSort($event)"
		:sortField="currentSortField"
		:sortOrder="currentSortOrder"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.DocumentClasses.DocumentClassFields') }) }}
		</template>

		<!-- Columns and Filter -->
		<Column :rowReorder="true" style="min-width: 2rem; max-width: 2rem;" />
		<Column field="id" header="ID" filterField="id" :sortable="true" :showFilterMenu="false" style="min-width: 4rem; max-width: 4rem;">
			<template #body="slotProps">
				{{slotProps.data.id}}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 3rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="name" filterField="name" :header="$t('Squeeze.DocumentClasses.Name')" :frozen="frozenColumns.name" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; z-index: 1;">
			<template #header>
				<i :class="frozenColumns.name ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 4.5rem;"
					class="frozenIcon"
					@click="setFrozenColumns('name')"
				></i>
			</template>
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.name">
					{{slotProps.data.name}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="description" filterField="description" :header="$t('Squeeze.DocumentClasses.Description')" :frozen="frozenColumns.description" :sortable="true" :showFilterMenu="false" style="min-width: 10rem; z-index: 1;">
			<template #header>
				<i :class="frozenColumns.description ? 'mdi mdi-lock-outline' : 'mdi mdi-lock-open-variant-outline'"
					style="margin-left: 8rem;"
					class="frozenIcon"
					@click="setFrozenColumns('description')"
				></i>
			</template>
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getTranslatedDescription(slotProps.data)">
					{{ getTranslatedDescription(slotProps.data) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="dataType" filterField="dataType" :header="$t('Squeeze.DocumentClasses.DataType')" :sortable="true" :showFilterMenu="false" style="min-width: 9rem; max-width: 9rem;">
			<template #body="slotProps">
				{{ getTranslationFromDataType(slotProps.data.dataType) }}
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="dataTypes"
					class="p-column-filter filter-field"
					optionValue="id"
					optionLabel="text"
					:showClear="true"
					style="min-width: 8rem; max-width: 8rem;"
				/>
			</template>
		</Column>
		<Column field="defaultValue" filterField="defaultValue" :header="$t('Squeeze.DocumentClasses.DefaultValue')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.defaultValue">
					{{slotProps.data.defaultValue}}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="locatorId" filterField="locatorId" :header="$t('Squeeze.DocumentClasses.Locator')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getDescriptionFromLocator(slotProps.data.locatorId)">
					{{ getDescriptionFromLocator(slotProps.data.locatorId) }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<Dropdown
					v-model="filterModel.value"
					@change="filterCallback()"
					:options="locators"
					class="p-column-filter filter-field"
					optionValue="id"
					:optionLabel="getTranslatedDescription"
					:showClear="true"
					:filter="true"
					:autoFilterFocus="true"
					style="min-width: 10rem;"
					@show="showDropdownOverlay"
				/>
			</template>
		</Column>
		<Column field="subFieldName" filterField="subFieldName" :header="$t('Squeeze.DocumentClasses.Subfield')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<!-- FIXME: Currently the Translation is taken from i18n. Generally the correct name should be returned from the API -->
				<template v-if="$te('Squeeze.Locators.SubFields.' + slotProps.data.subFieldName)">
					<div class="column-cell" v-tooltip.bottom="$t('Squeeze.Locators.SubFields.' + slotProps.data.subFieldName)">
						{{ $t('Squeeze.Locators.SubFields.' + slotProps.data.subFieldName) }}
					</div>
				</template>
				<template v-else>
					<div class="column-cell" v-tooltip.bottom="slotProps.data.subFieldName">
						{{ slotProps.data.subFieldName }}
					</div>
				</template>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<!--
		<Column field="fieldGroupId" :header="$t('Squeeze.DocumentClasses.Group')" :sortable="false" filterMatchMode="contains">
			<template #body="slotProps">
				{{ getDescriptionFromFieldGroup(slotProps.data.fieldGroupId) }}
			</template>
		</Column>
		-->
		<Column field="externalName" filterField="externalName" :header="$t('Squeeze.DocumentClasses.ExternalName')" :sortable="true" :showFilterMenu="false" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.externalName">
					{{ slotProps.data.externalName }}
				</div>
			</template>
			<template #filter="{filterModel, filterCallback}">
				<InputText style="min-width: 10rem;" class="p-column-filter filter-field" type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" />
			</template>
		</Column>
		<Column field="mandatory" :header="$t('Squeeze.DocumentClasses.Mandatory')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'mandatory')" v-model="slotProps.data.mandatory" :binary="true" />
			</template>
		</Column>
		<Column field="readonly" :header="$t('Squeeze.DocumentClasses.ReadOnly')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'readonly')" v-model="slotProps.data.readonly" :binary="true" />
			</template>
		</Column>
		<Column field="hidden" :header="$t('Squeeze.DocumentClasses.Hidden')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'hidden')" v-model="slotProps.data.hidden" :binary="true" />
			</template>
		</Column>
		<Column field="forceValidation" :header="$t('Squeeze.DocumentClasses.ForceValidation')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'forceValidation')" v-model="slotProps.data.forceValidation" :binary="true" />
			</template>
		</Column>
		<Column field="sameLineAsPreviousField" :header="$t('Squeeze.DocumentClasses.ShowFieldNextToPreviousField')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 11rem; max-width: 11rem; text-align: center; word-break: initial;">
			<template #header>
				<Badge class="mdi mdi-crosshairs-question" v-tooltip="$t('Squeeze.DocumentClasses.CheckboxTooltipSameLineField')"></Badge>
			</template>
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'sameLineAsPreviousField')" v-model="slotProps.data.sameLineAsPreviousField" :binary="true" />
			</template>
		</Column>
		<Column field="trainable" :header="$t('Squeeze.DocumentClasses.Trainable')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'trainable')" v-model="slotProps.data.trainable" :binary="true" :disabled="slotProps.data.name === 'Creditor'" />
			</template>
		</Column>
		<Column field="useInReport" :header="$t('Squeeze.DocumentClasses.UseInReport')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 11rem; max-width: 11rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data, 'useInReport')" v-model="slotProps.data.useInReport" :binary="true" />
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
			<template #body="slotProps">
				<Button v-tooltip.bottom="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-lg p-button-rounded p-button-success p-button-text" @click="openEntryDialog(slotProps.data)" />
				<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
			</template>
		</Column>

		<template #footer>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="openEntryDialog({})" />
			<Button v-if="isFilterActive" :label="$t('Squeeze.General.ClearFilters')" type="button" icon="mdi mdi-filter-off-outline" class="p-button p-ml-2" @click="clearFilters"/>
			<Button v-if="store.state.featureSet.validationFieldLayout" :label="$t('Squeeze.DocumentClasses.FieldsLayout')" type="button" icon="mdi mdi-view-compact-outline" class="p-button-text" @click="openFieldsLayout" :disabled="!rows.length" />
			<Button v-tooltip.bottom="$t('Squeeze.Validation.Buttons.ResetTableSettings')" type="button" icon="mdi mdi-sort-variant-remove" class="p-button p-button-text p-ml-2" @click="resetTableSettings" />
		</template>
	</DataTable>

	<!-- Lösch-Dialog -->
	<DialogDelete :showDialog="deleteDialog" @onClose="deleteDialog = false" @onConfirm="deleteEntry()" />
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
/* eslint max-lines: off */
import {computed, defineComponent, onMounted, PropType, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import useSqueezeStore from "@/apps/squeeze/store";
import Tooltip from "primevue/tooltip";
import DataTable from "@/components/datatable/DexDataTable.vue";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import DialogDelete from "@/components/DialogDelete.vue";
import Badge from "primevue/badge";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import {DocumentField, DocumentFieldGroup, DocumentLocator} from "@dex/squeeze-client-ts";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {useRoute} from "vue-router";
import {TableSettings} from "@/util/TableSettings";
import {ClientManager} from "@/singletons/ClientManager";

interface FrozenColumns {
	name: boolean;
	description: boolean;
}

export default defineComponent({
	name: 'DocumentClassFieldTable',
	components: {
		DataTable,
		Column,
		Checkbox,
		DialogDelete,
		Badge,
		InputText,
		Dropdown,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All rows */
		rows: {
			type: Array as PropType<DocumentField[]>,
			default: () => [],
		},
		/** All available Locators */
		locators: {
			type: Array as PropType<DocumentLocator[]>,
			default: () => [],
		},
		/** All Field-Groups for the Document-Class */
		fieldGroups: {
			type: Array as PropType<DocumentFieldGroup[]>,
			default: () => [],
		},
		fieldGroupId: {
			type: Number,
			default: -1,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["openEntryDialog", "deleteEntry", "onChangeCheckbox", "onChangeSortOrder", "openFieldsLayout"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Vuex-Store */
		const store = useSqueezeStore();
		const route = useRoute();

		/** User API for persisting table settings */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current sort field */
		const currentSortField = ref<string | undefined>(undefined);

		/** Current sort order */
		const currentSortOrder = ref<number>(0);

		/** show Delete Dialog? */
		const deleteDialog = ref<boolean>(false);

		/** Data of selected row */
		const rowData = reactive<DocumentField>({});

		/** Is the Search activated? */
		const searchActive = ref<boolean>(false);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Is Filtering active? */
		const isFilterActive = ref<boolean>(false);

		/** List of Data-Types */
		const dataTypes = computed(() => {
			return [
				{
					id: 'Text',
					text: t('Squeeze.General.Text'),
				},
				{
					id: 'Date',
					text: t('Squeeze.General.Date'),
				},
				{
					id: 'Amount',
					text: t('Squeeze.General.Amount'),
				},
			];
		});

		/** Which columns are frozen? */
		const frozenColumns = reactive<FrozenColumns>({
			name: true,
			description: true,
		});

		/** Set frozen columns */
		const setFrozenColumns = (column: string) => {
			(frozenColumns as any)[column] = !(frozenColumns as any)[column];
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'frozenColumns', String(props.fieldGroupId), { ...frozenColumns });
		}

		/**
		 * Opens the Delete Dialog
		 * @param {DocumentField} data Data from the Document-Class that should be deleted
		 */
		const openDeleteDialog = (data: DocumentField) => {
			Object.assign(rowData, data);
			deleteDialog.value = true;
		}

		/**
		 * Opens the Edit-Dialog
		 * @param {DocumentField} data Data to edit
		 */
		const openEntryDialog = (data: DocumentField) => {
			if (!data.lookup) {
				data.lookup = {
					active: false,
					allowCustomValues: true,
					tableId: 0,
					resultKeyColumnId: 0,
					searchColumnIds: [],
					resultValueColumnIds: [],
					minInputLength: 1,
				};
			}

			// reset data
			if (!data.id) {
				data.id = undefined;
				data.name = undefined;
				data.description = undefined;
				data.dataType = undefined;
				data.documentClassId = undefined;
				data.locatorId = undefined;
				data.mandatory = undefined;
				data.readonly = undefined;
				data.hidden = undefined;
				data.forceValidation = undefined;
				data.sortOrder = undefined;
				data.fieldGroupId = undefined;
				data.externalName = undefined;
				data.value = undefined;
				data.defaultValue = undefined;
				data.sameLineAsPreviousField = undefined;
				data.subFieldName = undefined;
				data.state = undefined;
			}

			Object.assign(rowData, data);
			emit("openEntryDialog", rowData, props.fieldGroupId);
		}

		/**
		 * Gets the Description of a locator
		 * @param {number} locatorId Id of the Locator to get the Description from
		 * @return {string} Description of the Locator
		 */
		const getDescriptionFromLocator = (locatorId: number) => {
			if (!locatorId) {
				return "";
			}

			if (!props.locators) {
				return String(locatorId);
			}

			const locator = props.locators.find(locator => locator.id === locatorId);
			if (!locator) {
				return String(locatorId);
			}

			return getTranslatedDescription(locator);
		}

		/**
		 * Get the Translation of data type
		 * @param {string} type
		 * @return {string} Current Translation of data type
		 */
		const getTranslationFromDataType = (type: string): string => {
			switch (type) {
			case 'Text':
				return t('Squeeze.General.Text');
			case 'Date':
				return t('Squeeze.General.Date');
			case 'Amount':
				return t('Squeeze.General.Amount');
			default:
				return type;
			}
		}

		/**
		 * Gets the Description of a Field Group
		 * @param {number} fieldGroupId Id of the Locator to get the Description from
		 * @return {string} Description of the Locator
		 */
		const getDescriptionFromFieldGroup = (fieldGroupId: number) => {
			if (!props.fieldGroups) {
				return String(fieldGroupId)
			}

			const fieldGroup = props.fieldGroups.find(fieldGroup => fieldGroup.id === fieldGroupId);
			if (!fieldGroup) {
				return String(fieldGroupId);
			}
			return getTranslatedDescription(fieldGroup);
		}

		/** Emits the "deleteEntry"-Event */
		const deleteEntry = () => {
			emit("deleteEntry", rowData);
		}

		/** Triggered when a checkbox is clicked
		 * @param {DocumentField} data Data to edit
		 * @param {string} fieldName Current field name
		 * @package fieldName Name of the current field
		 */
		const onChangeCheckbox = (data: DocumentField, fieldName: string) => {
			emit("onChangeCheckbox", data, fieldName);
		}

		/**
		 * Triggered when the rows are reordered
		 * @param event
		 */
		const onRowReorder = (event: any) => {
			const orderedList: number[] = [];
			event.value.forEach((documentClass: DocumentField) => {
				orderedList.push(documentClass.id!);
			});

			emit("onChangeSortOrder", orderedList, props.fieldGroupId, isFilterActive.value);
		}

		/** Opens the fields layout */
		const openFieldsLayout = () => {
			emit("openFieldsLayout");
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			isFilterActive.value = event.filteredValue.length < props.rows.length;
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"name": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"description": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"dataType": {value: null, matchMode: FilterMatchMode.EQUALS},
				"defaultValue": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"locatorId": {value: null, matchMode: FilterMatchMode.EQUALS},
				"subFieldName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"externalName": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		initFilters();

		/** Triggered on sort a column */
		const onSort = (event: { sortField: string; sortOrder: number }) => {
			currentSortField.value = event.sortField;
			currentSortOrder.value = event.sortOrder;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', String(props.fieldGroupId), { field: event.sortField, order: event.sortOrder }, v => !v || v.order === 0);
		}

		/** Reset sort and frozen column settings */
		const resetTableSettings = () => {
			currentSortField.value = undefined;
			currentSortOrder.value = 0;
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'sort', String(props.fieldGroupId), { field: '', order: 0 }, v => !v || v.order === 0);
			Object.assign(frozenColumns, { name: true, description: true });
			TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name as string, 'frozenColumns', String(props.fieldGroupId), null, () => true);
		}

		onMounted(() => {
			const savedSort = TableSettings.getTableSubSetting<{ field: string; order: number }>(store, route.name as string, 'sort', String(props.fieldGroupId));
			if (savedSort) {
				currentSortField.value = savedSort.field;
				currentSortOrder.value = savedSort.order;
			}

			const savedFrozenColumns = TableSettings.getTableSubSetting<Record<string, boolean>>(store, route.name as string, 'frozenColumns', String(props.fieldGroupId));
			if (savedFrozenColumns) {
				Object.assign(frozenColumns, savedFrozenColumns);
			}
		});

		return {
			getTranslatedDescription,
			t,
			toast,
			store,
			deleteDialog,
			rowData,
			searchActive,
			filters,
			isFilterActive,
			dataTypes,
			frozenColumns,
			setFrozenColumns,
			showDropdownOverlay,
			openDeleteDialog,
			openEntryDialog,
			getDescriptionFromLocator,
			getTranslationFromDataType,
			getDescriptionFromFieldGroup,
			deleteEntry,
			onChangeCheckbox,
			onRowReorder,
			openFieldsLayout,
			onFilter,
			initFilters,
			clearFilters,
			currentSortField,
			currentSortOrder,
			onSort,
			resetTableSettings,
		};
	},
});

</script>

<style lang="scss" scoped>

::v-deep(i.p-datatable-reorderablerow-handle.pi.pi-bars) {
	cursor: grab;
}

::v-deep(i.p-datatable-reorderablerow-handle.pi.pi-bars:active) {
	cursor: grabbing;
}

::v-deep(.p-badge) {
	background-color: transparent;
	max-width: 1.5rem;
	order: 2;
}

.mdi {
	line-height: 1.5rem;
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

// Table header
.p-datatable ::v-deep(.p-datatable-thead) {
	z-index: 2;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th) {
	background-color: var(--dex-primary-light-color);
	padding: 0.6rem 0.6rem 0.2rem 0.3rem;
	border-bottom-color: var(--dex-primary-light-color);
	color: var(--dex-text-color);
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead tr th:first-child) {
	padding: 0.6rem 0.6rem 0.2rem 0.6rem;
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-sortable-column-icon) {
	color: var(--dex-text-color);

	&:hover {
		color: var(--dex-text-color);
	}
}

.p-datatable.p-datatable-documents ::v-deep(.p-datatable-thead .p-inputtext.filter-field) {
	padding: 0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);
	border-style: solid;
	border-width: 0.063rem;

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}
}

.p-datatable ::v-deep(.p-datatable-thead .p-dropdown.filter-field) {
	padding:0.4rem;
	background-color: transparent;
	color: var(--dex-text-color);
	border-radius: 0;
	border-color: var(--filter-field-color);

	&:focus {
		border-color: rgba(255, 255, 255, 0.8);
	}

	.p-dropdown-label {
		padding: 0 1rem 0 0.4rem;
		color: var(--filter-field-color);
	}
}

span.p-badge.p-badge-dot.mdi.mdi-crosshairs-question {
	color: var(--dex-text-color);
	cursor: pointer;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

.frozenIcon {
	color: var(--dex-text-color);
	position: absolute;

	&:hover {
		color: var(--text-color);
		opacity: 0.75;
	}
}

</style>
